/**
 * Slovak Post pickup widget.
 *
 * The carrier's script renders a map into an element the caller owns and
 * answers over `window.postMessage`. Its messages are JSON *strings*, and the
 * page receives everybody else's messages too, so every frame is decoded
 * through a schema and matched against {@link SLOVAK_POST_HANDSHAKE_TOKEN}.
 *
 * @see https://www.posta.sk/pickup-widget/docs/
 */
import { Context, Data, Schema } from 'effect';
import type { Effect } from 'effect';

import type { SlovakPostWidgetSource } from '../../config/slovakPost';
import { ErrorTag } from '../../data/error';
import { ContextTag } from '../context';
import { PickupCarrier, type PickupPoint } from '../shipping/PickupPoint';
import { decodePlacementId } from '../shipping/PlacementId';

/**
 * The id `lib.js` and its dialog iframe stamp on every frame they exchange.
 *
 * It is compiled into the carrier's bundle — the same constant in `lib.js` and
 * `lib-test1.js` — so it identifies the widget's own frames on a shared
 * `message` stream. It is a protocol constant, not a merchant credential: the
 * widget's `open()` takes no token, and no configured value ever appears in a
 * frame.
 */
export const SLOVAK_POST_HANDSHAKE_TOKEN = '9dad2878-0eab-410f-98e2-370691c54059';

/**
 * Which kinds of branch the map offers.
 *
 * - `office` — a post office
 * - `bbox` — a BalíkoBOX parcel locker
 * - `point` — a PoštaPOINT partner counter
 */
export const SlovakPostBranchKind = {
  OFFICE: 'office',
  BBOX: 'bbox',
  POINT: 'point',
} as const;

export type SlovakPostBranchKind = (typeof SlovakPostBranchKind)[keyof typeof SlovakPostBranchKind];

export const SLOVAK_POST_DEFAULT_BRANCH_KINDS: readonly SlovakPostBranchKind[] = [
  SlovakPostBranchKind.OFFICE,
  SlovakPostBranchKind.BBOX,
  SlovakPostBranchKind.POINT,
];

/**
 * This value carries Slovenská pošta routing for senders that print labels.
 * The manual calls it "Smerovanie zásielky, použite ak si vytvárate vlastné adresné štítky".
 * Both fields are optional. The carrier does not document the HSS and OSS abbreviations.
 *
 * The frontend does not send this field to the backend.
 * It preserves the value for consumers that print their own labels.
 *
 * @see https://www.posta.sk/pickup-widget/docs/
 */
export type SlovakPostRoutingT = {
  readonly hss?: string;
  readonly oss?: string;
};

export type SlovakPostT = {
  readonly id: string;
  readonly name: string;
  readonly address: string;
  readonly city: string;
  readonly zip: string;
  readonly routing?: SlovakPostRoutingT;
};

/**
 * Slovak Post routes parcels by postcode. The backend therefore stores the postcode.
 * The branch id identifies the counter. It does not identify the delivery destination.
 * The carrier defines `deliveryAddress` as the recipient address data.
 *
 * Sending `id` misroutes the parcel.
 * The browser test uses different `id` and `zip` values to pin this mapping.
 *
 * @see https://www.posta.sk/pickup-widget/docs/
 */
export const toPickupPoint = (point: SlovakPostT): PickupPoint => ({
  carrier: PickupCarrier.SlovakPost,
  placementId: decodePlacementId(point.zip),
  name: point.name,
  street: point.address,
  city: point.city,
  zip: point.zip,
});

/**
 * Enough of a frame to tell "ours" from "somebody else's". A frame that fails
 * this is not the carrier talking, and is dropped without comment.
 */
export const SlovakPostEnvelopeSchema = Schema.Struct({
  token: Schema.String,
  op: Schema.Literals(['select', 'close']),
});

/** The selection itself. A frame that passes the envelope and fails this is a protocol breach. */
export const SlovakPostSelectionSchema = Schema.Struct({
  params: Schema.Struct({
    branch: Schema.Struct({
      id: Schema.String,
      label: Schema.String,
      deliveryAddress: Schema.Struct({
        street: Schema.String,
        city: Schema.String,
        zip: Schema.NonEmptyString,
      }),
      routing: Schema.optionalKey(
        Schema.Struct({
          hss: Schema.optionalKey(Schema.String),
          oss: Schema.optionalKey(Schema.String),
        })
      ),
    }),
  }),
});

export type SlovakPostSelection = Schema.Schema.Type<typeof SlovakPostSelectionSchema>;

export const toSlovakPostT = (selection: SlovakPostSelection): SlovakPostT => ({
  id: selection.params.branch.id,
  name: selection.params.branch.label,
  address: selection.params.branch.deliveryAddress.street,
  city: selection.params.branch.deliveryAddress.city,
  zip: selection.params.branch.deliveryAddress.zip,
  // Absent for a branch the carrier reports no routing for, and the key stays
  // absent rather than becoming `undefined`, so the value compares by equality.
  ...(selection.params.branch.routing === undefined
    ? {}
    : { routing: selection.params.branch.routing }),
});

export class SlovakPostWidgetNotLoadedError extends Data.TaggedError(
  ErrorTag.SlovakPostWidgetNotLoadedError
)<{
  message: string;
}> {}

export class SlovakPostWidgetCancelledError extends Data.TaggedError(
  ErrorTag.SlovakPostWidgetCancelledError
)<{
  message: string;
}> {}

export class SlovakPostWidgetScriptLoadError extends Data.TaggedError(
  ErrorTag.SlovakPostWidgetScriptLoadError
)<{
  message: string;
  cause?: unknown;
}> {}

export class SlovakPostWidgetPayloadError extends Data.TaggedError(
  ErrorTag.SlovakPostWidgetPayloadError
)<{
  message: string;
  cause?: unknown;
}> {}

export type SlovakPostWidgetError =
  | SlovakPostWidgetNotLoadedError
  | SlovakPostWidgetCancelledError
  | SlovakPostWidgetScriptLoadError
  | SlovakPostWidgetPayloadError;

export type SlovakPostWidgetOptions = {
  /** CSS selector of the element the map fills. It must already be in the document. */
  readonly embedSelector: string;
  readonly branchKinds?: readonly SlovakPostBranchKind[];
};

/**
 * What the carrier's script puts on `globalThis` as `slposta`.
 *
 * It is looked up and narrowed at the call site rather than declared as a
 * `Window` augmentation. An augmentation from a shared package lands in every
 * consumer's global scope, and collides with any other declaration of the same
 * name — which is exactly what the tenant's own legacy declaration does today.
 *
 * @see https://www.posta.sk/pickup-widget/docs/
 */
export type SlovakPostPickupWidgetConfig = {
  /**
   * CSS selector (or element) of the element the map fills. Without it the
   * widget opens as a fullscreen overlay of its own, over the whole page.
   */
  readonly embedElement: string | Element;
  readonly branchKinds?: readonly SlovakPostBranchKind[];
  readonly uiHideCloseButton?: boolean;
};

export type SlovakPostPickupWidget = {
  readonly open: (config: SlovakPostPickupWidgetConfig) => void;
  readonly close: () => void;
};

export type SlovakPostWidgetService = {
  readonly pick: (
    source: SlovakPostWidgetSource,
    options: SlovakPostWidgetOptions
  ) => Effect.Effect<SlovakPostT, SlovakPostWidgetError>;
  readonly close: Effect.Effect<void>;
};

export class SlovakPostWidget extends Context.Service<SlovakPostWidget, SlovakPostWidgetService>()(
  ContextTag.SlovakPostWidget
) {}
